/* eslint-disable import/prefer-default-export */
import axios from '../axios';

// ---------------------------------------------------------------------------
// Financeiro / Vendas a prazo & adimplência — Service Layer (handoff 0084)
//
// Os 5 endpoints de /billing/analytics/* que alimentam o dashboard de crediário.
// São INDEPENDENTES entre si e devem ser chamados em PARALELO (cada um responde
// em dezenas de ms) — a orquestração é da página, não daqui.
//
// Camada fina sobre o axios singleton: desembrulha o envelope JSend
// (`data.data.<chave>`) e propaga o erro cru. O tratamento (dicionário de
// `code`, fieldErrors, error_id) fica na página via
// `pages/Finance/utils/apiError.js` — padrão vigente do módulo enquanto a infra
// global FC1/FC4 não está ligada.
//
// A tradução camelCase (args) → snake_case (query params) acontece SÓ aqui. Os
// campos do PAYLOAD ficam como o contrato entrega (snake_case em PT-BR:
// `emitido`, `a_vencer`, `share_da_carteira_pct`, `data_quality`) — mesma
// política do customersApi: simetria com o backend nas linhas de dado.
//
// Permissão: `finance:dashboard:read`. Sem escopo por empresa — o filtro de
// CNPJ (`bank_account_id`) é navegação, não trava de acesso.
// ---------------------------------------------------------------------------

// Arrays de id viram CSV ("4,7") — contrato canônico dos filtros do billing
// (handoff 0082, reafirmado no 0084 §3). Vazio = param ausente = SEM filtro.
//
// A lista aceita, além dos ids, o token `none` (handoff 0086 §2.2), que
// significa "registros sem classificação" (`origin_id IS NULL`). Ele COMPÕE com
// os ids em vez de ser um modo à parte:
//
//   4,7      →  origin_id IN (4,7)
//   none     →  origin_id IS NULL
//   4,none   →  (origin_id IN (4) OR origin_id IS NULL)
//
// Por isso o serializador não valida o conteúdo: quem decide o que é token
// válido é o backend, que agora responde 422 em token malformado (`abc`, `0`,
// `-1`) em vez de descartar em silêncio. Sanitizar aqui só esconderia o erro.
function toCsv(values) {
   if (!Array.isArray(values) || !values.length) return undefined;
   return values.join(',');
}

// Filtros comuns aos 5 endpoints. TODOS opcionais: omitir = não filtrar (base
// inteira, 2022 → hoje). Nada de default injetado aqui — em especial `as_of`,
// que o backend resolve no fuso do domínio (America/Sao_Paulo) e devolve em
// `reference_date`. "Hoje" nunca é calculado no cliente.
function buildAnalyticsParams(filters = {}) {
   const { from, to, asOf, originIds, bankAccountIds, dateBasis } = filters;

   const params = {};
   if (from) params.from = from;
   if (to) params.to = to;
   if (asOf) params.as_of = asOf;

   const originCsv = toCsv(originIds);
   if (originCsv) params.origin_id = originCsv;

   const bankAccountCsv = toCsv(bankAccountIds);
   if (bankAccountCsv) params.bank_account_id = bankAccountCsv;

   // Lente, não filtro: `current` (vencimento vigente, com prorrogações) vs.
   // `original` (pactuado). Altera adimplência, aging e vintage ao mesmo tempo.
   if (dateBasis) params.date_basis = dateBasis;

   return params;
}

// ===========================================================================
// 1. SUMMARY — KPIs das dobras 1 e 2
// ===========================================================================
//
// Devolve `emitido`, `ticket_medio`, `recebido`, `adimplencia_em_dia`,
// `recuperacao`, `atraso_medio_dias`, `dso_dias`, `cancelado`,
// `reference_date`, `date_basis` e `data_quality`.
//
// `reducao_negociada` foi REMOVIDO do contrato: 89% do que ele somava vinha de
// dois títulos com erro de vírgula. Era correção de digitação com nome de
// política financeira.
//
// ⚠️ `*_pct`, `atraso_medio_dias` e `dso_dias` podem vir `null` (sem
// denominador). null ≠ 0 — a tela renderiza "—", nunca 0/0%/NaN.
export async function getAnalyticsSummary(filters = {}) {
   const response = await axios.get('/billing/analytics/summary', {
      params: buildAnalyticsParams(filters),
   });
   return response?.data?.data?.summary ?? null;
}

// ===========================================================================
// 2. AGING — foto da carteira em aberto numa data
// ===========================================================================
//
// Devolve `a_vencer`, `buckets[]`, `vencido`, `total_em_aberto`.
// Os 4 buckets (1-30, 31-60, 61-90, 90+) vêm SEMPRE, mesmo zerados e em ordem
// estável — a barra empilhada renderiza sem tratar ausência.
export async function getAnalyticsAging(filters = {}) {
   const response = await axios.get('/billing/analytics/aging', {
      params: buildAnalyticsParams(filters),
   });
   return response?.data?.data?.aging ?? null;
}

// ===========================================================================
// 3. BREAKDOWN — onde nasce e onde concentra
// ===========================================================================
//
// `by` é OBRIGATÓRIO: 'origin' | 'bank_account' | 'customer'. `limit` 1..50
// (default 10 no backend).
//
// Devolve `total`, `rows[]`, `outros` (|null) e `sem_classificacao` (|null).
// `sem_classificacao` é a carga histórica sem origem — em `by=origin` na base
// inteira são ~71% do valor. A tela é obrigada a exibi-lo como linha própria.
export async function getAnalyticsBreakdown(filters = {}) {
   const { by, limit, ...rest } = filters;

   const params = buildAnalyticsParams(rest);
   if (by) params.by = by;
   if (limit) params.limit = limit;

   const response = await axios.get('/billing/analytics/breakdown', { params });
   return response?.data?.data?.breakdown ?? null;
}

// ===========================================================================
// 4. FORECAST — previsão de recebimento
// ===========================================================================
//
// `horizons`: 1 a 6 valores, cada um 1..365 (default do backend: 7,15,30).
// ⚠️ Os horizontes são CUMULATIVOS a partir de hoje — o de 30 inclui o de 7.
// Barras lado a lado, nunca empilhadas.
//
// Devolve `horizons[]` e `vencido_acumulado`.
export async function getAnalyticsForecast(filters = {}) {
   const { horizons, ...rest } = filters;

   const params = buildAnalyticsParams(rest);
   const horizonsCsv = toCsv(horizons);
   if (horizonsCsv) params.horizons = horizonsCsv;

   const response = await axios.get('/billing/analytics/forecast', { params });
   return response?.data?.data?.forecast ?? null;
}

// ===========================================================================
// 5. VINTAGE — safra
// ===========================================================================
//
// `thresholdDias` 1..365 (default 60); `months` 1..8 valores, cada um 1..60
// (default [1,2,3,6,12] — a tela pede [1,3,6,12]).
//
// Devolve `threshold_dias`, `months`, `safras[]` com `pontos[]`.
// ⚠️ Ponto no futuro vem `pct: null`, NUNCA 0: "ainda não deu tempo de saber".
// Não plotar, não interpolar, não tratar como zero. Safra `carga_historica`
// (<= 2026-02) é marcada visualmente (linha tracejada).
export async function getAnalyticsVintage(filters = {}) {
   const { thresholdDias, months, ...rest } = filters;

   const params = buildAnalyticsParams(rest);
   if (thresholdDias) params.threshold_dias = thresholdDias;

   const monthsCsv = toCsv(months);
   if (monthsCsv) params.months = monthsCsv;

   const response = await axios.get('/billing/analytics/vintage', { params });
   return response?.data?.data?.vintage ?? null;
}
